import { useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { Tooltip as ReactTooltip } from "react-tooltip";
import ApplicationContext from "../context/application/ApplicationContext";
import TenantContext from "../context/tenant/TenantContext";
import AuthContext from "../context/auth/AuthContext";
import useTranslator from "../hooks/useTranslator";
import SkorDefaultLoader from "../assets/img/skorDefaultLoader.gif";
import { haveValue, isValidEmail, toast } from "../helper/common";
import {
  getInitials,
  resolveParticipantName,
} from "../helper/applicationHelpers";
import UserIcon from "../assets/img/user-icon.svg";
import SkorProtectSelector from "./SkorProtectSelector/SkorProtectSelector";
import SkorProtectBadge from "./SkorProtectBadge/SkorProtectBadge";
import CreateGroupScreen from "./CreateGroupScreen/CreateGroupScreen";

/* ── small avatar circle ── */
const Avatar = ({ name, size = 40, bg = "#a3eb64", color = "#1E2528" }) => (
  <div
    style={{
      width: size,
      height: size,
      minWidth: size,
      borderRadius: "50%",
      background: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      fontSize: size * 0.35,
      color,
      textTransform: "uppercase",
    }}
  >
    {getInitials(name)}
  </div>
);

const resolveCreatorName = (created_by) => {
  if (!created_by || typeof created_by === "string") return "";
  const fname = created_by.fname || created_by.firstname || "";
  const lname = created_by.lname || created_by.lastname || "";
  return `${fname} ${lname}`.trim() || created_by.email || "";
};

const resolveCreatorEmail = (created_by) => {
  if (!created_by || typeof created_by === "string") return null;
  return created_by.email ?? null;
};

const isSharedWithLandlord = (app, landlordId) => {
  if (!landlordId) return false;
  const extracted_landlord_id = landlordId?.split?.("_")?.[0] ?? landlordId;
  const inShareRequests = (app.shareRequests ?? []).some(
    (entry) => entry?.landlord_id?._id === extracted_landlord_id
  );
  if (inShareRequests) return true;
  const inSharedProfiles = (app.sharedProfiles ?? []).some(
    (sp) => sp?.id?.landlordid?._id === extracted_landlord_id
  );
  return inSharedProfiles;
};

/* ── SCREEN 1 – choice ── */
const ChoiceScreen = ({
  landlordData,
  applicationToShare,
  onSelectIndividual,
  onSelectApplication,
  onSelectCreate,
  onClose,
  processing,
  individualSharing,
  _t,
  user,
}) => {
  const landlordName =
    `${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() ||
    landlordData?.email ||
    _t("landlord", "Landlord");
  const individualProfileTitle =
    typeof user?.application_title === "string" && user.application_title.trim()
      ? user.application_title.trim()
      : _t("myIndividualProfile", "My individual profile");

  return (
    <div className="isf-modal">
      {/* Dark header */}
      <div className="isf-header">
        <button
          className="isf-close-btn"
          onClick={onClose}
          disabled={processing}
          aria-label="close"
        >
          {processing ? "..." : "✕"}
        </button>
        <div className="isf-header-landlord">
          <Avatar name={landlordName} size={44} bg="#a3eb64" color="#1E2528" />
          <div className="isf-header-landlord-info">
            <span className="isf-header-landlord-name">{landlordName}</span>
            <span className="isf-header-landlord-sub">
              {_t(
                "hasInvitedYouToShareYourProfile",
                "has invited you to share your profile"
              )}
            </span>
          </div>
        </div>
        <h2 className="isf-header-title">
          {_t(
            "whatWouldYouLikeToShare",
            "What would you like to share with this landlord?"
          )}
        </h2>
      </div>

      {/* Options */}
      <div className="isf-options">
        {/* Individual profile */}
        <button
          className={`isf-option-card${individualSharing ? " isf-option-card-sharing" : ""}`}
          onClick={onSelectIndividual}
          disabled={individualSharing}
          aria-busy={individualSharing}
        >
          <div className="isf-option-icon isf-option-icon-person">
            <img src={UserIcon} alt="User" />
          </div>
          <div className="isf-option-text">
            <span className="isf-option-title">
              {individualSharing
                ? _t("sharing", "Sharing...")
                : individualProfileTitle}
            </span>
            <span className="isf-option-sub">
              {_t("shareOnlyYourOwnProfile", "Share only your own profile.")}
            </span>
          </div>
          <svg
            className="isf-chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        {/* Latest application */}
        {(applicationToShare ? [applicationToShare] : []).map((app) => {
          const participants = app.participants ?? [];
          return (
            <div
              key={app._id}
              className={`isf-option-card isf-option-card-group${processing ? " isf-option-card-sharing" : ""}`}
              aria-disabled={processing}
            >
              <div
                className="isf-option-card-inner"
                style={processing ? { pointerEvents: "none" } : undefined}
                onClick={() => !processing && onSelectApplication(app)}
              >
                <div className="isf-option-icon isf-option-icon-group">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <div className="isf-option-text">
                  <span className="isf-option-title">{app.title}</span>
                  <div className="isf-chips-row">
                    {participants.slice(0, 4).map((p, i) => {
                      const name = resolveParticipantName(p);
                      const isMe =
                        p.email?.toLowerCase() === user?.email?.toLowerCase();
                      return (
                        <div
                          key={i}
                          className={`isf-chip${isMe ? " isf-chip-me" : ""}`}
                          title={name}
                        >
                          {isMe ? "You" : getInitials(name)}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <svg
                  className="isf-chevron"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            </div>
          );
        })}

        {/* Create new group */}
        <button
          className="isf-option-card isf-option-card-create"
          onClick={onSelectCreate}
          disabled={processing}
        >
          <div className="isf-option-icon isf-option-icon-create">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </div>
          <div className="isf-option-text">
            <span className="isf-option-title">
              {_t("createANewGroup", "Create a new group")}
            </span>
            <span className="isf-option-sub">
              {_t(
                "inviteYourFlatmatesByEmail",
                "Invite your flatmates by email."
              )}
            </span>
          </div>
          <svg
            className="isf-chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/* ── SCREEN 1b – individual profile + skor protect ── */
const IndividualScreen = ({
  landlordData,
  onBack,
  onConfirm,
  processing,
  _t,
}) => {
  const [skorProtect, setSkorProtect] = useState(true);
  const landlordName =
    `${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() ||
    landlordData?.email ||
    _t("landlord");

  return (
    <div className="isf-modal isf-modal-sub">
      <div className="isf-sub-header">
        <button className="isf-back-btn" onClick={onBack}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          {_t("back")}
        </button>
        <div className="isf-sub-title-block">
          <h2 className="isf-sub-title">{_t("shareYourIndividualProfile")}</h2>
          <span className="isf-sub-landlord">
            {_t("with")} {landlordName}
          </span>
        </div>
      </div>

      <div className="isf-body">
        <SkorProtectSelector value={skorProtect} onChange={setSkorProtect} />
      </div>

      <div className="isf-footer">
        <button
          className="isf-btn isf-btn-outline"
          onClick={onBack}
          disabled={processing}
        >
          {_t("back")}
        </button>
        <button
          className="isf-btn isf-btn-primary"
          onClick={() => onConfirm(skorProtect)}
          disabled={processing}
        >
          {processing ? "..." : _t("shareProfile")}
        </button>
      </div>
    </div>
  );
};

/* ── SCREEN 2 – join group ── */
export { Avatar, resolveCreatorEmail };
const JoinGroupScreen = ({
  app,
  landlordData,
  onBack,
  onClose,
  onAccept,
  onDecline,
  processing,
  user,
  _t,
}) => {
  const creatorName = resolveCreatorName(app.created_by);
  const creatorEmail = resolveCreatorEmail(app.created_by);
  const participants = app.participants ?? [];
  const landlordName =
    `${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() ||
    _t("theLandlord", "the landlord");

  const getRole = (p) => {
    const isOwner =
      creatorEmail && p.email?.toLowerCase() === creatorEmail.toLowerCase();
    if (isOwner) return "owner";
    const isMe = p.email?.toLowerCase() === user?.email?.toLowerCase();
    if (isMe) return "pending";
    return p.consent_status ?? "pending";
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "owner":
        return _t("groupOwner", "Group owner");
      case "pending":
        return _t("pendingAcceptance", "Pending acceptance");
      case "accepted":
        return _t("accepted", "Accepted");
      case "rejected":
        return _t("rejected", "Rejected");
      default:
        return role;
    }
  };

  return (
    <div className="isf-modal isf-modal-sub">
      {/* Sub-screen header */}
      <div className="isf-sub-header">
        {onBack && (
          <button className="isf-back-btn" onClick={onBack}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
            {_t("back", "Back")}
          </button>
        )}
        <div className="isf-sub-title-block">
          <h2 className="isf-sub-title">
            {`${_t("join")} ${creatorName}'s ${_t("group")}`}
          </h2>
          {landlordData && (
            <span className="isf-sub-landlord">
              {_t("with", "with")} {landlordName}
            </span>
          )}
        </div>
      </div>

      {/* Members section */}
      <div className="isf-body">
        <div className="isf-members-card">
          <div className="isf-members-card-header">
            <span className="isf-members-label">
              {_t("groupMembers", "Group members")}
            </span>
            {app.skor_protect && (
              <>
                <span
                  data-tooltip-id="isf-sp-badge-tooltip"
                  className="d-flex align-items-center"
                >
                  <SkorProtectBadge />
                </span>
                <ReactTooltip
                  id="isf-sp-badge-tooltip"
                  place="bottom"
                  className="isf-sp-tooltip-dark"
                  render={() => (
                    <span className="isf-sp-tooltip-text">
                      {participants?.[0]?.name +
                        " " +
                        _t("skorProtectGroupTooltip")}
                    </span>
                  )}
                />
              </>
            )}
          </div>
          <div className="isf-members-list">
            {participants.map((p, i) => {
              const name = resolveParticipantName(p);
              const isMe =
                p.email?.toLowerCase() === user?.email?.toLowerCase();
              const role = getRole(p);
              return (
                <div key={i} className="isf-member-row">
                  <Avatar
                    name={isMe ? "You" : name}
                    size={40}
                    bg={role === "owner" ? "#a3eb64" : "#d8d8d8"}
                    color={role === "owner" ? "#1E2528" : "#555"}
                  />
                  <div className="isf-member-info">
                    <span className="isf-member-name">
                      {isMe ? _t("you", "You") : name}
                    </span>
                    <span className="isf-member-role">
                      {getRoleLabel(role)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="isf-join-disclaimer">
          {landlordData
            ? _t("byAcceptingYourProfileWillBeShared")
            : _t("byAcceptingYouWillJoinGroup")}
        </p>
      </div>

      {/* Footer */}
      <div className="isf-footer">
        <button
          className="isf-btn isf-btn-outline"
          onClick={onDecline}
          disabled={processing}
        >
          {_t("decline", "Decline")}
        </button>
        <button
          className="isf-btn isf-btn-primary"
          onClick={onAccept}
          disabled={processing}
        >
          {processing ? "..." : _t("acceptAndJoin", "Accept & join")}
        </button>
      </div>
    </div>
  );
};

/* ── MAIN COMPONENT ── */
const InvitationShareModal = ({
  isOpen,
  landlordId, // cookies.requestProfileLink — may become undefined after cookies clear
  propertyId, // cookies.property_id
  onClose,
  onClearCookies,
  onNeedsConsent,
  onDone,
}) => {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);
  const { getLandlordsConsentInfo, loadSharedProfiles } =
    useContext(TenantContext);
  const {
    pendingApplications,
    myApplications,
    createApplication,
    consentApplication,
    loadApplicationsData,
    openLandlordLink,
    loadLandlordRequests,
    acceptLandlordRequest,
  } = useContext(ApplicationContext);

  const currentLang = localStorage.getItem("i18nextLng").toLowerCase() || "en";

  const defaultTitle =
    currentLang === "es"
      ? `Aplicación de ${user?.fname}`
      : `${user?.fname}'s Application`;

  const [step, setStep] = useState("choice");
  const [selectedApp, setSelectedApp] = useState(null);
  const [landlordData, setLandlordData] = useState(null);
  const [landLordLoading, setLandlordLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [individualSharing, setIndividualSharing] = useState(false);
  // Snapshot IDs so parent cookie-clearing doesn't affect in-flight actions
  const capturedLandlordId = useRef(null);
  const requestId = useRef(null);
  const fetchedRef = useRef(false);
  // true when user arrived via the signup flow (tenantSignupFlow cookie was set)
  const isSignupFlowRef = useRef(false);
  const [signupCookies, , removeSignupCookie] = useCookies([
    "tenantSignupFlow",
  ]);

  useEffect(() => {
    if (isOpen && landlordId && !fetchedRef.current) {
      fetchedRef.current = true;
      // Snapshot before cookies are cleared
      capturedLandlordId.current = landlordId;
      const capturedPropertyId = propertyId;
      // Capture signup-flow flag before clearing cookies
      isSignupFlowRef.current = haveValue(signupCookies.tenantSignupFlow);

      setLandlordLoading(true);

      Promise.all([
        getLandlordsConsentInfo(landlordId),
        openLandlordLink(landlordId, capturedPropertyId),
        loadApplicationsData(),
      ])
        .then(([consentRes, linkRes]) => {
          setLandlordData(consentRes?.data ?? consentRes);
          // Store the request ID returned by the API
          requestId.current =
            linkRes?.data?._id ??
            linkRes?.success_res?._id ??
            linkRes?._id ??
            null;
          // Clear cookies now that the link has been registered
          onClearCookies();
          removeSignupCookie("tenantSignupFlow", { path: "/" });
          // Refresh landlord requests to surface any notifications
          return loadLandlordRequests();
        })
        .catch(() => {
          // Still clear cookies on failure to avoid re-triggering
          onClearCookies();
          removeSignupCookie("tenantSignupFlow", { path: "/" });
        })
        .finally(() => setLandlordLoading(false));
    }
    if (!isOpen) {
      fetchedRef.current = false;
      setStep("choice");
      setSelectedApp(null);
      setIndividualSharing(false);
      requestId.current = null;
      capturedLandlordId.current = null;
      isSignupFlowRef.current = false;
    }
  }, [isOpen, landlordId]);

  // Signup flow: filter to apps already shared with this landlord (flatmate added you).
  // Already-logged-in flow: skip the landlord filter, just surface the latest pending app.
  const pendingAppsForLandlord = isSignupFlowRef.current
    ? (() => {
        const filtered = (pendingApplications ?? []).filter((app) =>
          isSharedWithLandlord(app, capturedLandlordId.current ?? landlordId)
        );

        filtered.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        return filtered.slice(0, 1);
      })()
    : (() => {
        const sorted = [...(pendingApplications ?? [])].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        return sorted.slice(0, 1);
      })();

  const applicationToShare = [
    ...(myApplications ?? []),
    ...pendingAppsForLandlord,
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

  const handleClose = () => {
    setStep("choice");
    setSelectedApp(null);
    onClose();
  };

  /* returns true if landlord requires consent the tenant hasn't given yet */
  const consentRequired = () => {
    if (!landlordData) return false;
    const insuranceEnabled = landlordData?.insurance_service_status;
    const affidavitEnabled = landlordData?.affidavit_activated;
    const hasInsuranceConsent =
      haveValue(user?.consents?.gdpr_consent) &&
      haveValue(user?.consents?.skor_consent);
    const hasAffidavitConsent = haveValue(user?.consents?.declaration_consent);
    return (
      (insuranceEnabled && !hasInsuranceConsent) ||
      (affidavitEnabled && !hasAffidavitConsent)
    );
  };

  /*
   * Gate every share action behind consent.
   * If consent is needed: close this modal, let parent show ConsentStepModal
   * with the captured action as the post-consent callback.
   * If consent is not needed: run action directly.
   */
  const withConsentGate = async (action) => {
    if (consentRequired()) {
      onNeedsConsent(landlordData, action);
      handleClose();
      return;
    }
    await action();
  };

  /* Individual profile → respond to landlord request with optional skor_protect */
  const handleIndividual = async (skorProtect = true) => {
    if (!requestId.current) return;
    const reqId = requestId.current;
    const userId = user?._id ?? localStorage.getItem("saveduserid");
    const shareAction = async () => {
      try {
        setProcessing(true);
        await acceptLandlordRequest(reqId, null, skorProtect);
        if (userId) await loadSharedProfiles(userId, "full");
        onDone();
      } catch {
        toast.error(_t("failedToShareProfile"));
      } finally {
        setProcessing(false);
      }
    };
    await withConsentGate(shareAction);
  };

  const handleApplication = async (application) => {
    if (!requestId.current || !application?._id) return;
    const reqId = requestId.current;
    const userId = user?._id ?? localStorage.getItem("saveduserid");
    const shareAction = async () => {
      try {
        setProcessing(true);
        await acceptLandlordRequest(reqId, application._id);
        if (userId) await loadSharedProfiles(userId, "full");
        onDone();
      } catch {
        toast.error(_t("failedToShareProfile"));
      } finally {
        setProcessing(false);
      }
    };
    await withConsentGate(shareAction);
  };

  /* Cross button on choice screen → share individual profile before closing, same as the individual-share flow */
  const handleChoiceClose = async () => {
    if (!requestId.current) {
      handleClose();
      return;
    }
    await handleIndividual();
  };

  /* Join existing group → consent to the application + respond to landlord request */
  const handleAcceptGroup = async () => {
    if (!selectedApp || !requestId.current) return;
    const appId = selectedApp._id;
    const reqId = requestId.current;
    const acceptAction = async () => {
      try {
        setProcessing(true);
        const actions = [consentApplication(appId, "accepted")];
        actions.push(acceptLandlordRequest(reqId, appId));
        await Promise.all(actions);
        const userId = user?._id ?? localStorage.getItem("saveduserid");
        if (userId) await loadSharedProfiles(userId, "full");
        onDone();
      } catch {
        toast.error("Failed to join group");
      } finally {
        setProcessing(false);
      }
    };
    await withConsentGate(acceptAction);
  };

  /* Decline → reject the application invitation */
  const handleDeclineGroup = async () => {
    if (!selectedApp) {
      setStep("choice");
      setSelectedApp(null);
      return;
    }
    const appId = selectedApp._id;
    try {
      setProcessing(true);
      await consentApplication(appId, "rejected");
      const userId = user?._id ?? localStorage.getItem("saveduserid");
      if (userId) await loadSharedProfiles(userId, "full");
    } catch {
      toast.error("Failed to decline");
    } finally {
      setProcessing(false);
    }
    setStep("choice");
    setSelectedApp(null);
  };

  /*
   * Create group → create application first, then respond to landlord request
   * with the new application ID.
   * Consent is checked before any action — participants list is captured in closure.
   */
  const handleCreateAndShare = async (participants, skorProtect = true) => {
    if (!requestId.current) return;
    const reqId = requestId.current;
    const createAction = async () => {
      try {
        const res = await createApplication({
          participants,
          title: defaultTitle,
          skor_protect: skorProtect,
        });
        const appId = res?.success_res?._id ?? res?._id;
        await acceptLandlordRequest(reqId, appId);
        const userId = user?._id ?? localStorage.getItem("saveduserid");
        await Promise.all([
          loadApplicationsData(),
          userId ? loadSharedProfiles(userId, "full") : Promise.resolve(),
        ]);
        onDone();
      } catch (err) {
        toast.error(
          err?.response?.data?.message ??
            _t("failedToCreateGroup", "Failed to create group")
        );
        throw err;
      }
    };
    await withConsentGate(createAction);
  };

  if (!isOpen) return null;

  return (
    <div className="wrapper-modal isf-overlay">
      <div className="isf-container" onClick={(e) => e.stopPropagation()}>
        {landLordLoading ? (
          <div className="isf-loading">
            <img
              src={SkorDefaultLoader}
              className="defaultSkorLoader"
              alt="Example"
            />
          </div>
        ) : step === "choice" ? (
          <ChoiceScreen
            landlordData={landlordData}
            pendingAppsForLandlord={pendingAppsForLandlord}
            applicationToShare={applicationToShare}
            onSelectIndividual={async () => {
              if (user?.skor_protect) {
                setIndividualSharing(true);
                try {
                  await handleIndividual(true);
                } finally {
                  setIndividualSharing(false);
                }
                return;
              }
              setStep("individual");
            }}
            onSelectGroup={(app) => {
              setSelectedApp(app);
              setStep("join-group");
            }}
            onSelectApplication={(app) => {
              const isPending = pendingAppsForLandlord.some(
                (pendingApp) => pendingApp._id === app._id
              );
              if (isPending) {
                setSelectedApp(app);
                setStep("join-group");
                return;
              }
              handleApplication(app);
            }}
            onSelectCreate={() => setStep("create-group")}
            onClose={handleChoiceClose}
            _t={_t}
            user={user}
            processing={processing}
            individualSharing={individualSharing}
          />
        ) : step === "individual" ? (
          <IndividualScreen
            landlordData={landlordData}
            onBack={() => setStep("choice")}
            onConfirm={handleIndividual}
            processing={processing}
            _t={_t}
          />
        ) : step === "join-group" ? (
          <JoinGroupScreen
            app={selectedApp}
            landlordData={landlordData}
            onBack={() => setStep("choice")}
            onClose={handleClose}
            onAccept={handleAcceptGroup}
            onDecline={handleDeclineGroup}
            processing={processing}
            user={user}
            _t={_t}
          />
        ) : (
          <CreateGroupScreen
            onBack={() => setStep("choice")}
            onClose={handleClose}
            onCreateAndShare={handleCreateAndShare}
            user={user}
          />
        )}
      </div>
    </div>
  );
};

export { isSharedWithLandlord, JoinGroupScreen };
export default InvitationShareModal;
